import { useEffect, useRef, useState } from "react";
import UseTourContext from "../../context/TourContext";
import UserInfo from "../../context/UserAccessContext";
import { useTour } from "@reactour/tour";
import { useLocation, useNavigate } from "react-router-dom";
import { PostmortemServices } from "../../services/postmortemServices";
import { postmortemApiEndpoint } from "../../config";

export const UseNavBar = () => {
  const { setIsOpen } = useTour();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    setCurrentTouringPage,
    refreshTouringSteps,
  } = UseTourContext();

  const {
    graphData,
  } = UserInfo();

  const { fetchPaginatedPostMortemData, fetchArchivedTableData } = PostmortemServices();

  const handleTour = () => {
    setIsOpen(true);
  };

  // --- Incident ID search (ongoing + archived combined results) ---
  // Navbar remounts on navigation, so seed from the term the last search navigated with.
  const [incidentSearchValue, setIncidentSearchValue] = useState(location.state?.searchIncidentId ?? "");
  const [incidentSearchResults, setIncidentSearchResults] = useState([]);
  const [isSearchingIncident, setIsSearchingIncident] = useState(false);
  const [showIncidentResults, setShowIncidentResults] = useState(false);
  const [isSuggestingIncident, setIsSuggestingIncident] = useState(false);

  const suggestTimer = useRef(null);
  const latestSearchId = useRef(0);
  const archivedCache = useRef(null);
  useEffect(() => () => clearTimeout(suggestTimer.current), []);

  // Typeahead: show similar incidents as the user types, without navigating.
  const handleIncidentSearchChange = (value) => {
    setIncidentSearchValue(value);
    clearTimeout(suggestTimer.current);
    if (!value?.trim()) {
      latestSearchId.current++;
      setIncidentSearchResults([]);
      setShowIncidentResults(false);
    } else if (value.trim().length >= 3) {
      suggestTimer.current = setTimeout(() => handleIncidentSearchSubmit(value, true), 300);
    }
  };

  // Searches both ongoing and archived postmortems for the given Incident ID
  // and returns a combined, tagged list of matches (does not remove either
  // data source - results from both are merged together).
  const handleIncidentSearchSubmit = async (rawValue, suggestOnly = false) => {
    clearTimeout(suggestTimer.current);
    const searchId = ++latestSearchId.current;
    const incidentId = (rawValue ?? incidentSearchValue ?? "").trim();
    if (!incidentId) {
      setIncidentSearchResults([]);
      setShowIncidentResults(false);
      return [];
    }

    // The input is disabled while searching, so typeahead must not toggle it (it would drop focus).
    if (suggestOnly) {
      setIsSuggestingIncident(true);
      setShowIncidentResults(true);
    } else {
      setIsSearchingIncident(true);
    }
    const needle = incidentId.toLowerCase();
    let ongoingMatches = [];
    let archivedMatches = [];

    try {
      const ongoingResult = await fetchPaginatedPostMortemData(
        postmortemApiEndpoint,
        1,
        10,
        { incidentId }
      );
      const ongoingList =
        ongoingResult?.content || ongoingResult?.data || ongoingResult || [];
      if (Array.isArray(ongoingList)) {
        // Incomplete postmortems are archived; skip them here so they aren't listed twice (also covers a backend not yet filtering them).
        ongoingMatches = ongoingList
          .filter((pm) => !pm?.postMortemIncomplete)
          .map((pm) => ({ ...pm, _source: "ongoing" }));
      }
    } catch (err) {
      // Ongoing lookup failed; still attempt archived below.
    }

    // Show ongoing matches right away; the archived list (fetched once) can be slow.
    if (suggestOnly && searchId === latestSearchId.current) setIncidentSearchResults(ongoingMatches);

    // Archived list is fetched once and cached; partial IDs match so similar incidents are offered.
    try {
      if (!archivedCache.current) archivedCache.current = (await fetchArchivedTableData()) || [];
      archivedMatches = archivedCache.current
        .filter(
          (pm) =>
            // The endpoint returns every postmortem; only DONE or incomplete ones are archived (same as the
            // archived page, whose rows show incomplete > status > state).
            (pm?.postMortemIncomplete || (pm?.status || pm?.state) === "DONE") &&
            (pm?.incidentID?.toLowerCase?.().includes(needle) ||
              pm?.customID?.toLowerCase?.().includes(needle))
        )
        .slice(0, 10)
        .map((pm) => ({ ...pm, _source: "archived" }));
    } catch (err) {
      // Archived lookup failed; keep whatever ongoing matches were found.
    }

    const combinedResults = [...ongoingMatches, ...archivedMatches];
    if (suggestOnly) {
      if (searchId === latestSearchId.current) setIsSuggestingIncident(false);
    } else {
      setIsSearchingIncident(false);
    }
    // A newer keystroke/search superseded this one; drop its results.
    if (searchId !== latestSearchId.current) return combinedResults;

    // Generic search behavior: if there's exactly one match, go straight to
    // the right page (archived -> archived list, ongoing -> ongoing list)
    // without requiring an extra click on a dropdown item.
    if (!suggestOnly && combinedResults.length === 1) {
      setIncidentSearchResults(combinedResults);
      setShowIncidentResults(false);
      handleSelectIncidentResult(combinedResults[0], incidentId);
      return combinedResults;
    }

    setIncidentSearchResults(combinedResults);
    setShowIncidentResults(true);
    return combinedResults;
  };

  const handleSelectIncidentResult = (result, searchTerm) => {
    setShowIncidentResults(false);
    // Prefer the picked result's own ID so a partial typed term doesn't filter to a wider list.
    const idToPass = (result?.incidentID || searchTerm || incidentSearchValue || "").trim();
    setIncidentSearchValue(idToPass);
    // Same URL/state the table's report link uses (see NavBarHandlers).
    navigate(`/report?incidentID=${idToPass}&postmortemID=${result?.customID}`, {
      state: { customID: result?.customID, incidentID: idToPass },
    });
  };

  const closeIncidentResults = () => {
    setShowIncidentResults(false);
  };

  useEffect(() => {
    if (typeof setCurrentTouringPage === "function") {
      setCurrentTouringPage(location.pathname);
    }
  }, [location.pathname, refreshTouringSteps, setCurrentTouringPage]);

  return {
    location,
    handleTour,
    graphData,
    incidentSearchValue,
    incidentSearchResults,
    isSearchingIncident,
    isSuggestingIncident,
    showIncidentResults,
    handleIncidentSearchChange,
    handleIncidentSearchSubmit,
    handleSelectIncidentResult,
    closeIncidentResults,
  };
};
